import { ReactNode } from 'react';
import styled from 'styled-components';

import { Button } from './Button';

/**
 * Универсальный inline-баннер для предупреждений типа «фоновая задача
 * не дала ожидаемый результат, вот причина». Используется в ContractCard
 * (compose-protocol fallback) и ClaimCard (compose-response/redirect
 * fallback). До этого компонента было два разных inline-стиля с
 * хардкодом `#fff4e0`/`#c47800`, которые на тёмной теме превращались в
 * нечитаемое жёлто-бежевое пятно.
 *
 * Цвета берутся из `theme.colors.status.warning`:
 *   - в светлой теме это #F57C00 (мягкий оранжевый),
 *   - в тёмной теме это #FFB74D (светло-янтарный для контраста на
 *     тёмном фоне).
 * Фон собирается через `rgba(warning, 0.12)` — мягкий полупрозрачный
 * акцент, читаемый на обеих темах.
 */

const Wrapper = styled.div<{ $tone: 'warning' | 'danger' | 'info' }>`
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme, $tone }) => {
    const c = theme.colors.status[$tone];
    return `color-mix(in srgb, ${c} 16%, ${theme.colors.backgroundElevated} 84%)`;
  }};
  border-left: 3px solid
    ${({ theme, $tone }) => theme.colors.status[$tone]};

  & > strong {
    color: ${({ theme, $tone }) => theme.colors.status[$tone]};
  }
`;

const Body = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  white-space: pre-wrap;
`;

const Actions = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
`;

interface Props {
  /** Заголовок жирным сверху (опционально, иначе только тело). */
  title?: string;
  /** Само сообщение. Поддерживает многострочный текст. */
  children: ReactNode;
  /** Callback на «Скрыть»; если не задан — кнопка не показывается. */
  onDismiss?: () => void;
  /** Цветовая палитра. По умолчанию warning. */
  tone?: 'warning' | 'danger' | 'info';
  /** Текст кнопки скрытия. По умолчанию «Скрыть». */
  dismissLabel?: string;
}

export function FallbackBanner({
  title,
  children,
  onDismiss,
  tone = 'warning',
  dismissLabel = 'Скрыть',
}: Props) {
  return (
    <Wrapper role="alert" $tone={tone}>
      {title && <strong>{title}</strong>}
      <Body>{children}</Body>
      {onDismiss && (
        <Actions>
          <Button type="button" $size="sm" $variant="ghost" onClick={onDismiss}>
            {dismissLabel}
          </Button>
        </Actions>
      )}
    </Wrapper>
  );
}
